import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import InstanceList from './components/InstanceList';
import ScheduleManager from './components/ScheduleManager';
import AdminDashboard from './components/AdminDashboard';
import Login from './components/Login';
import { authService } from './services/authService';
import { apiService } from './services/apiService';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const authStatus = await authService.checkAuthStatus();
      setIsAuthenticated(authStatus.isAuthenticated);
      setUser(authStatus.user);
      
      // 인증된 경우 API 서비스에 토큰 설정
      if (authStatus.isAuthenticated && authStatus.token) {
        apiService.setToken(authStatus.token);
      }
    } catch (error) {
      console.error('인증 상태 확인 오류:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (credentials) => {
    try {
      const result = await authService.signIn(credentials);
      if (result.success) {
        setIsAuthenticated(true);
        setUser(result.user);
        
        // API 서비스에 토큰 설정
        if (result.token) {
          apiService.setToken(result.token);
        }
        
        return { success: true };
      } else {
        // NEW_PASSWORD_REQUIRED 등의 챌린지 상황
        return result;
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      return { 
        success: false, 
        error: error.message || '로그인에 실패했습니다.' 
      };
    }
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      setIsAuthenticated(false);
      setUser(null);
      
      // API 서비스에서 토큰 제거
      apiService.setToken(null);
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Header user={user} onLogout={handleLogout} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard user={user} />} />
            <Route path="/instances" element={<InstanceList />} />
            <Route path="/schedule" element={<ScheduleManager />} />
            <Route 
              path="/admin" 
              element={
                user && user.isAdmin ? (
                  <AdminDashboard user={user} />
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">접근 권한 없음</h2>
                    <p className="text-gray-600 mb-4">관리자 권한이 필요한 페이지입니다.</p>
                    <button 
                      onClick={() => window.history.back()}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                    >
                      이전 페이지로 돌아가기
                    </button>
                  </div>
                )
              } 
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;